import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate, Link } from 'react-router-dom';
import './App.css';
import GoogleDrivePicker from './components/GoogleDrivePicker';
import SelectedFiles from './components/SelectedFiles';
import TaskStatus from './components/TaskStatus';
import AuthSuccess from './components/AuthSuccess';
import AuthError from './components/AuthError';
import LoginPage from './components/LoginPage';
import LogoutPage from './components/LogoutPage';
import SearchPage from './components/SearchPage';
import { DriveItem, UserProfile } from './types';
import { getCurrentUser, createTask } from './services/api';
import { ToastProvider, useToast } from './context/ToastContext';

// Header component with conditional logout button
const AppHeader: React.FC<{ user?: UserProfile | null }> = ({ user }) => {
  return (
    <header className="app-header">
      <h1>Google Drive Task Manager</h1>
      {user?.authenticated && (
        <div className="header-controls">
          <p className="user-info">Signed in as: {user.name || user.email || user.userId}</p>
          <div className="header-links">
            <Link to="/search" className="button search-button">Search</Link>
            <Link to="/logout" className="button logout-button">Logout</Link>
          </div>
        </div>
      )}
    </header>
  );
};

// Task detail component
const TaskDetail: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { addToast } = useToast();
  
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const userData = await getCurrentUser();
        setUser(userData);
        
        if (!userData.authenticated) {
          // Show toast explaining the redirect
          if (userData.tokenPresent) {
            addToast({
              type: 'warning',
              title: 'Session Invalid',
              message: 'Your authentication token is present but invalid. Please log in again.',
              duration: 5000
            });
          } else {
            addToast({
              type: 'error',
              title: 'Not Authenticated',
              message: 'Session cookies not found. Please log in to continue.',
              duration: 5000
            });
          }
          // Delay the redirect slightly to allow toast to be seen
          setTimeout(() => navigate('/login'), 1000);
        } else {
          setIsLoading(false);
        }
      } catch (err) {
        // If API call failed, show network error toast
        addToast({
          type: 'error',
          title: 'Authentication Error',
          message: 'Could not verify your authentication status. Network error or API unavailable.',
          duration: 5000
        });
        // Delay the redirect slightly to allow toast to be seen
        setTimeout(() => navigate('/login'), 1000);
      }
    };
    
    checkAuth();
  }, [navigate, addToast]);
  
  const handleReset = () => {
    navigate('/');
  };
  
  if (isLoading) {
    return <div className="app-container loading">Loading...</div>;
  }
  
  if (!taskId) {
    return <Navigate to="/" replace />;
  }
  
  return (
    <div className="app-container">
      <AppHeader user={user} />
      
      <main className="app-content">
        <TaskStatus taskId={taskId} />
        <button className="button" onClick={handleReset}>Start New Task</button>
      </main>
    </div>
  );
};

// Main dashboard component
const Dashboard: React.FC = () => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<DriveItem[]>([]);
  const navigate = useNavigate();
  const { addToast } = useToast();

  // Check if user is authenticated
  useEffect(() => {
    const checkAuth = async () => {
      try {
        setIsLoading(true);
        const userData = await getCurrentUser();
        setUser(userData);
        
        // If not authenticated, redirect to login with appropriate toast
        if (!userData.authenticated) {
          // Show toast explaining the redirect
          if (userData.tokenPresent) {
            addToast({
              type: 'warning',
              title: 'Session Invalid',
              message: 'Your authentication token is present but invalid. Please log in again.',
              duration: 5000
            });
          } else {
            addToast({
              type: 'error',
              title: 'Not Authenticated',
              message: 'Session cookies not found. Please log in to continue.',
              duration: 5000
            });
          }
          // Delay the redirect slightly to allow toast to be seen
          setTimeout(() => navigate('/login'), 1000);
        } else {
          setIsLoading(false);
        }
      } catch (err: any) {
        // If API call failed, show network error toast
        addToast({
          type: 'error',
          title: 'Authentication Error',
          message: 'Could not verify your authentication status. Network error or API unavailable.',
          duration: 5000
        });
        // Delay the redirect slightly to allow toast to be seen
        setTimeout(() => navigate('/login'), 1000);
      }
    };

    checkAuth();
  }, [navigate, addToast]);

  // Handle Google Drive file selection
  const handleFilesPicked = (files: DriveItem[]) => {
    setSelectedFiles(prevFiles => [...prevFiles, ...files]);
  };

  // Remove a file from selection
  const handleRemoveFile = (fileId: string) => {
    setSelectedFiles(prevFiles => prevFiles.filter(file => file.id !== fileId));
  };

  // Start a new task with selected files
  const handleStartTask = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one file');
      return;
    }

    try {
      setError(null);
      setIsLoading(true);
      const fileIds = selectedFiles.map(file => file.id);
      const response = await createTask(fileIds);
      
      // Navigate to the task detail page with the snake_case task_id
      navigate(`/task/${response.task_id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to start task');
      setIsLoading(false);
    }
  };

  // Loading state
  if (isLoading) {
    return <div className="app-container loading">Loading...</div>;
  }

  // Avoid showing this if we're already redirecting with toast
  if (!user?.authenticated) {
    return null;
  }

  return (
    <div className="app-container">
      <AppHeader user={user} />

      <main className="app-content">
        <section className="file-selector">
          <h2>Select Files</h2>
          <GoogleDrivePicker onFilesPicked={handleFilesPicked} />
          <SelectedFiles 
            files={selectedFiles} 
            onRemoveFile={handleRemoveFile} 
          />
          <button 
            className="button start-task-button" 
            onClick={handleStartTask}
            disabled={selectedFiles.length === 0 || isLoading}
          >
            Start Task
          </button>
          {isLoading && <p>Starting task...</p>}
          {error && <div className="error-message">{error}</div>}
        </section>
      </main>
    </div>
  );
};

// Main App component with routing
function App() {
  return (
    <Router>
      <ToastProvider>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/logout" element={<LogoutPage />} />
          <Route path="/task/:taskId" element={<TaskDetail />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/auth-success" element={<AuthSuccess />} />
          <Route path="/auth-error" element={<AuthError />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ToastProvider>
    </Router>
  );
}

export default App; 